import React, {useState, useEffect} from 'react';
import PropTypes from 'prop-types';
import Icon from './icons/Icon';
import MiaPlatformDataService from '../services/MiaPlatformDataService';
import './MobilePreviewPage.css';

/**
 * Mobile Preview Page Component
 * Full-screen mobile experience for previewing deployed applications
 * Optimized for viewing live applications on mobile devices
 */
const MobilePreviewPage = ({
  isOpen,
  onClose,
  onAskAI
}) => {
  const [selectedProject, setSelectedProject] = useState(() => {
    const saved = localStorage.getItem('mia_selectedProject');
    return saved ? JSON.parse(saved) : null;
  });
  
  const [selectedRevision, setSelectedRevision] = useState(() => {
    const saved = localStorage.getItem('mia_selectedRevision');
    return saved ? JSON.parse(saved) : null;
  });

  const [selectedTenant, setSelectedTenant] = useState(() => {
    const saved = localStorage.getItem('mia_selectedTenant');
    return saved ? JSON.parse(saved) : null;
  });

  const [projectConfig, setProjectConfig] = useState(null);
  const [configLoading, setConfigLoading] = useState(false);
  const [environments, setEnvironments] = useState([]);
  const [selectedEnvironment, setSelectedEnvironment] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [showIframe, setShowIframe] = useState(false);

  useEffect(() => {
    if (selectedProject && selectedRevision && isOpen) {
      loadProjectConfig();
    }
  }, [selectedProject, selectedRevision, isOpen]);

  useEffect(() => {
    if (projectConfig && projectConfig.environments) {
      const envList = projectConfig.environments.map(env => ({
        id: env.environmentId || env.envId,
        name: env.label || env.envId,
        description: env.description,
        cluster: env.cluster
      }));
      setEnvironments(envList);
      
      // Auto-select first environment
      if (envList.length > 0 && !selectedEnvironment) {
        setSelectedEnvironment(envList[0]);
      }
    }
  }, [projectConfig]);

  useEffect(() => {
    if (selectedEnvironment && projectConfig) {
      // Construct preview URL based on project configuration
      // This is a placeholder - adjust based on your actual URL structure
      const baseUrl = projectConfig.baseUrl || 'https://example.com';
      const url = `${baseUrl}/${selectedEnvironment.name}`;
      setPreviewUrl(url);
    }
  }, [selectedEnvironment, projectConfig]);

  const loadProjectConfig = async () => {
    try {
      setConfigLoading(true);
      const config = await MiaPlatformDataService.getMicroservices(
        selectedProject.id,
        selectedRevision.id
      );
      setProjectConfig(config);
    } catch (error) {
      console.error('Failed to load project config:', error);
    } finally {
      setConfigLoading(false);
    }
  };

  const handleOpenInBrowser = () => {
    if (previewUrl) {
      window.open(previewUrl, '_blank');
    }
  };

  const handleAskAIAboutPreview = () => {
    const prompt = `I'm previewing my application "${selectedProject.name}" in the ${selectedEnvironment.name} environment.\n\nEnvironment URL: ${previewUrl}\n\nCan you help me with questions about deployment, environment configuration, or troubleshooting the preview?`;
    
    if (onAskAI) {
      onAskAI(prompt);
    }
    onClose(); // Close the preview page to return to chat
  };

  const handleRefresh = () => {
    if (showIframe) {
      setShowIframe(false);
      setTimeout(() => setShowIframe(true), 100);
    }
  };

  if (!isOpen) return null;

  const hasProject = selectedProject && selectedRevision;

  return (
    <div className="mobile-preview-page">
      {/* Header */}
      <div className="mobile-preview-header">
        <button
          className="back-button"
          onClick={onClose}
          type="button"
          aria-label="Go back"
        >
          <Icon name="arrow-left" size={24} />
        </button>
        <h1 className="page-title">
          <Icon name="eye" size={24} />
          {'Preview'}
        </h1>
        <div className="header-actions">
          {showIframe && (
            <>
              <button
                className="icon-button"
                onClick={handleRefresh}
                type="button"
                aria-label="Refresh preview"
              >
                <Icon name="refresh" size={20} />
              </button>
              <button
                className="icon-button"
                onClick={handleAskAIAboutPreview}
                type="button"
                aria-label="Ask AI about preview"
                title="Ask AI"
              >
                <Icon name="message-circle" size={20} />
              </button>
            </>
          )}
        </div>
      </div>

      {/* Content */}
      <div className="mobile-preview-content">
        {!hasProject ? (
          <div className="empty-state">
            <Icon name="eye" size={64} />
            <h2>No Project Selected</h2>
            <p>Please select a project from the Code tab first to enable preview</p>
            <button
              className="select-project-btn"
              onClick={onClose}
              type="button"
            >
              <Icon name="arrow-left" size={18} />
              Go Back
            </button>
          </div>
        ) : configLoading ? (
          <div className="loading-state">
            <Icon name="spinner" size={48} />
            <p>Loading project configuration...</p>
          </div>
        ) : (
          <>
            {/* Project Info */}
            <div className="project-info-bar">
              <div className="info-item">
                <Icon name="folder" size={16} />
                <span>{selectedProject.name}</span>
              </div>
              <div className="info-divider">•</div>
              <div className="info-item">
                <Icon name="git-branch" size={16} />
                <span>{selectedRevision.name}</span>
              </div>
            </div>

            {/* Environment Selector */}
            {environments.length > 0 && (
              <div className="environment-section">
                <label className="section-label">Environment</label>
                <div className="environment-chips">
                  {environments.map((env) => (
                    <button
                      key={env.id}
                      className={`env-chip ${selectedEnvironment?.id === env.id ? 'active' : ''}`}
                      onClick={() => setSelectedEnvironment(env)}
                      type="button"
                    >
                      <Icon name="server" size={16} />
                      {env.name}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Preview Controls */}
            {selectedEnvironment && (
              <div className="preview-controls">
                <div className="url-display">
                  <Icon name="globe" size={16} />
                  <span className="url-text">{previewUrl || 'No URL configured'}</span>
                </div>
                
                <div className="control-buttons">
                  {!showIframe ? (
                    <button
                      className="primary-btn"
                      onClick={() => setShowIframe(true)}
                      type="button"
                      disabled={!previewUrl}
                    >
                      <Icon name="eye" size={18} />
                      Load Preview
                    </button>
                  ) : (
                    <button
                      className="secondary-btn"
                      onClick={() => setShowIframe(false)}
                      type="button"
                    >
                      <Icon name="eye-off" size={18} />
                      Hide Preview
                    </button>
                  )}
                  
                  <button
                    className="secondary-btn"
                    onClick={handleOpenInBrowser}
                    type="button"
                    disabled={!previewUrl}
                  >
                    <Icon name="external-link" size={18} />
                    Open in Browser
                  </button>
                </div>

                <div className="info-box">
                  <Icon name="info" size={16} />
                  <p>
                    For the best preview experience, open the application in your mobile browser. 
                    This allows full interaction with all features.
                  </p>
                </div>
              </div>
            )}

            {/* Preview Frame */}
            {showIframe && previewUrl && (
              <div className="preview-frame-container">
                <div className="frame-header">
                  <div className="frame-controls">
                    <span className="frame-indicator active" />
                    <span className="frame-url">{previewUrl}</span>
                  </div>
                </div>
                <iframe
                  className="preview-iframe"
                  src={previewUrl}
                  title="Application Preview"
                  sandbox="allow-scripts allow-same-origin allow-forms allow-popups allow-modals"
                />
              </div>
            )}

            {/* Environment Details */}
            {selectedEnvironment && !showIframe && (
              <div className="environment-details">
                <h3>Environment Details</h3>
                <div className="details-grid">
                  <div className="detail-item">
                    <div className="detail-label">Name</div>
                    <div className="detail-value">{selectedEnvironment.name}</div>
                  </div>
                  {selectedEnvironment.description && (
                    <div className="detail-item">
                      <div className="detail-label">Description</div>
                      <div className="detail-value">{selectedEnvironment.description}</div>
                    </div>
                  )}
                  {selectedEnvironment.cluster && (
                    <div className="detail-item">
                      <div className="detail-label">Cluster</div>
                      <div className="detail-value">{selectedEnvironment.cluster}</div>
                    </div>
                  )}
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

MobilePreviewPage.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onAskAI: PropTypes.func
};

export default MobilePreviewPage;
